import { registerSW as registerViteSW } from 'virtual:pwa-register';

/**
 * Service worker registration.
 *
 * Explicit rather than auto-injected, because the update behaviour matters:
 * an update must never replace the running application mid-set (Phase 0 §9.3).
 * `onNeedRefresh` records that an update is waiting; applying it is a decision
 * the UI makes later, and Phase 6 will suppress it entirely during a workout.
 */

export interface ServiceWorkerStatus {
  updateAvailable: boolean;
  offlineReady: boolean;
}

type StatusListener = (status: ServiceWorkerStatus) => void;

const status: ServiceWorkerStatus = { updateAvailable: false, offlineReady: false };
const listeners = new Set<StatusListener>();

function emit(): void {
  for (const listener of listeners) listener({ ...status });
}

export function onServiceWorkerStatus(listener: StatusListener): () => void {
  listeners.add(listener);
  listener({ ...status });
  return () => listeners.delete(listener);
}

let applyUpdate: ((reload?: boolean) => Promise<void>) | undefined;

/**
 * Phase C: reliable update detection.
 *
 * `onNeedRefresh` alone only fires when the *browser* happens to notice a
 * changed `sw.js` on some navigation/fetch of its own timing — for a
 * long-lived SPA session that never reloads, that can be hours or days late.
 * `registration.update()` is the explicit ask: "check the network for a new
 * worker now." Two triggers call it — a periodic timer for a session left
 * open, and `visibilitychange`, for the far more common case of a phone
 * coming back out of a pocket after a deploy happened while it was away.
 * Same idiom `syncScheduler.ts` already uses for its own periodic +
 * visibility-triggered cadence, deliberately mirrored here rather than
 * reinvented.
 *
 * A sensible interval, not an aggressive one — 45 minutes sits in the
 * middle of the 30–60 minute range this doesn't need to be more urgent than.
 */
export const UPDATE_CHECK_INTERVAL_MS = 45 * 60 * 1000;

let registration: ServiceWorkerRegistration | undefined;
let intervalId: ReturnType<typeof setInterval> | null = null;

function checkForUpdate(): void {
  // A registration only exists once `onRegisteredSW` has fired; harmless to
  // call before or after an update is already pending — `update()` is the
  // browser's own idempotent "is there anything newer" check.
  //
  // `.catch`, not `void` alone — integration hardening pass: `update()`
  // rejects on a network failure (offline, a transient blip), and this runs
  // unattended on a timer and on every `visibilitychange`. Without a handler,
  // every such check on a device that happens to be offline throws an
  // unhandled promise rejection. Log-and-continue is correct here: a failed
  // background check has nothing to recover *to* — it just tries again on
  // its own next trigger, exactly as it would if the check simply hadn't run
  // yet, so there is nothing for the user to see or act on.
  registration?.update().catch((error: unknown) => {
    console.warn('LiftX: service worker update check failed', error);
  });
}

function onVisibility(): void {
  if (document.visibilityState === 'visible') checkForUpdate();
}

/** Idempotent — calling this while already running is a no-op, not a second timer/listener. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  if (intervalId !== null) return;

  applyUpdate = registerViteSW({
    immediate: true,
    onNeedRefresh() {
      status.updateAvailable = true;
      emit();
    },
    onOfflineReady() {
      status.offlineReady = true;
      emit();
    },
    onRegisteredSW(_swUrl, reg) {
      registration = reg;
    },
  });

  intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibility);
}

/**
 * Symmetric with `registerServiceWorker()`, the same way `stopSyncScheduler`
 * is with `startSyncScheduler` — nothing in production calls this today: the
 * interval and listener are meant to run for the tab's whole lifetime, and
 * the browser tears both down for free on a real document unload. It exists
 * so update-detection tests can reset between cases without resetting the
 * whole module.
 */
export function stopServiceWorkerUpdateChecks(): void {
  if (intervalId !== null) clearInterval(intervalId);
  intervalId = null;
  registration = undefined;
  document.removeEventListener('visibilitychange', onVisibility);
}

/** Apply a waiting update and reload. Called only on explicit user action. */
export async function applyPendingUpdate(): Promise<void> {
  if (applyUpdate === undefined) return;
  await applyUpdate(true);
}
